import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import client from "../../../shared/api/client";
import { toastSuccess, toastError } from "../../../shared/toastHelper";

const SORT_OPTIONS = [
  { value: "newest", label: "Newest to Oldest" },
  { value: "oldest", label: "Oldest to Newest" },
  { value: "a-z", label: "A to Z" },
  { value: "z-a", label: "Z to A" },
];

const POLL_INTERVAL = 3000;

const sortEvents = (events, sortBy) => {
  const sorted = [...events];
  switch (sortBy) {
    case "a-z":
      return sorted.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
    case "z-a":
      return sorted.sort((a, b) => (b.title || "").localeCompare(a.title || ""));
    case "oldest":
      return sorted.sort((a, b) => new Date(a.eventDate || 0) - new Date(b.eventDate || 0));
    case "newest":
    default:
      return sorted.sort((a, b) => new Date(b.eventDate || 0) - new Date(a.eventDate || 0));
  }
};

const useMetaSocialAds = ({ teamId, initialEvents, initialSummary, initialConnectionStatus }) => {
  const [events, setEvents] = useState(initialEvents || []);
  const [summary, setSummary] = useState(initialSummary || {});
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [sortBy, setSortBy] = useState("newest");
  const [syncStatus, setSyncStatus] = useState(initialSummary?.syncStatus || "idle");
  const [connectionStatus, setConnectionStatus] = useState(initialConnectionStatus);
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const pollSyncStatus = useCallback(async () => {
    try {
      const response = await client.get(`/teams/${teamId}/meta_social_ads/sync_status`);
      const data = response.data;
      setSyncStatus(data.syncStatus);
      setSummary((prev) => ({ ...prev, syncError: data.syncError }));
      if (data.connectionStatus) setConnectionStatus(data.connectionStatus);

      // "backfilling" means matching + recent insights are ready and only historical
      // spend is still loading in the background — stop polling and surface the results
      // rather than polling for the (possibly hours-long) backfill.
      if (data.syncStatus === "idle" || data.syncStatus === "backfilling") {
        stopPolling();
        setTimeout(async () => {
          try {
            const fresh = await client.get(`/teams/${teamId}/meta_social_ads/sync_status`);
            setSummary(fresh.data.summary);
            setEvents(fresh.data.events);
          } catch {
            setSummary(data.summary);
            setEvents(data.events);
          }
        }, 1000);
        toastSuccess(
          data.syncStatus === "backfilling"
            ? "Sync complete — historical spend is still loading in the background"
            : "Sync complete"
        );
      } else if (data.syncStatus === "failed") {
        stopPolling();
        toastError(data.syncError || "Sync failed");
      }
    } catch {
      stopPolling();
    }
  }, [teamId, stopPolling]);

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(pollSyncStatus, POLL_INTERVAL);
  }, [stopPolling, pollSyncStatus]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: start polling if syncing on page load
    if (initialSummary?.syncStatus === "syncing") {
      startPolling();
    }
    return stopPolling;
  }, []);

  const fetchMatches = useCallback(
    async (eventId) => {
      setMatchesLoading(true);
      try {
        const response = await client.get(
          `/teams/${teamId}/calendar_events/${eventId}/meta_ad_event_matches`
        );
        setMatches(response.data);
      } catch {
        toastError("Failed to load ad matches");
        setMatches([]);
      } finally {
        setMatchesLoading(false);
      }
    },
    [teamId]
  );

  const handleSelectEvent = useCallback(
    (eventId) => {
      setSelectedEventId(eventId);
      fetchMatches(eventId);
    },
    [fetchMatches]
  );

  const handleCreateMatch = useCallback(
    async (adId) => {
      try {
        await client.post(
          `/teams/${teamId}/calendar_events/${selectedEventId}/meta_ad_event_matches`,
          { meta_ad_event_match: { meta_ad_id: adId } }
        );
        toastSuccess("Ad match added");
        setEvents((prev) =>
          prev.map((e) =>
            e.id === selectedEventId
              ? { ...e, matchCount: e.matchCount + 1 }
              : e
          )
        );
        fetchMatches(selectedEventId);
      } catch {
        toastError("Failed to add ad match");
      }
    },
    [teamId, selectedEventId, fetchMatches]
  );

  const handleRemoveMatch = useCallback(
    async (matchId) => {
      try {
        await client.delete(
          `/teams/${teamId}/calendar_events/${selectedEventId}/meta_ad_event_matches/${matchId}`
        );
        toastSuccess("Ad match removed");
        setEvents((prev) =>
          prev.map((e) =>
            e.id === selectedEventId
              ? { ...e, matchCount: Math.max(0, e.matchCount - 1) }
              : e
          )
        );
        fetchMatches(selectedEventId);
      } catch {
        toastError("Failed to remove ad match");
      }
    },
    [teamId, selectedEventId, fetchMatches]
  );

  const handleResync = useCallback(async () => {
    setSyncStatus("syncing");
    try {
      await client.post(`/teams/${teamId}/meta_social_ads/resync`);
      startPolling();
    } catch (error) {
      setSyncStatus("idle");
      toastError(error?.response?.data?.error || "Failed to start sync");
    }
  }, [teamId, startPolling]);

  const sortedEvents = useMemo(() => sortEvents(events, sortBy), [events, sortBy]);
  const selectedEvent = events.find((e) => e.id === selectedEventId);

  return {
    events: sortedEvents,
    summary,
    selectedEventId,
    selectedEvent,
    matches,
    matchesLoading,
    sortBy,
    setSortBy,
    sortOptions: SORT_OPTIONS,
    handleSelectEvent,
    handleCreateMatch,
    handleRemoveMatch,
    handleResync,
    syncStatus,
    connectionStatus,
  };
};

export default useMetaSocialAds;
